import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import deTranslations from '../locales/de/index';

export const APP_LANGUAGES = ['de', 'en', 'es', 'it', 'fr'] as const;
export type AppLanguage = (typeof APP_LANGUAGES)[number];

export type TranslationBundle = Record<string, unknown>;
export type LocaleResources = Record<string, TranslationBundle>;

interface LocaleModule {
  default: unknown;
}

interface BuildManifestFile {
  path: string;
}

interface BuildManifest {
  files: BuildManifestFile[];
  localesHash?: string;
}

type LazyAppLanguage = Exclude<AppLanguage, 'de'>;
const loadedLocales = new Set<AppLanguage>(['de']);
const localeLoadPromises = new Map<AppLanguage, Promise<void>>();

const localeLoaders: Record<LazyAppLanguage, () => Promise<LocaleModule>> = {
  en: () => import('../locales/en/index'),
  es: () => import('../locales/es/index'),
  it: () => import('../locales/it/index'),
  fr: () => import('../locales/fr/index'),
};

function reportI18nError(context: string, error: unknown): void {
  const normalizedError = error instanceof Error ? error : new Error(String(error));
  console.error(`[i18n] ${context}`, normalizedError);
  void captureI18nException(normalizedError, context);
}

async function captureI18nException(error: Error, context: string): Promise<void> {
  try {
    const sentry = await import('@sentry/react');
    sentry.captureException(error, {
      tags: { subsystem: 'i18n', context },
    });
  } catch (monitoringError) {
    const normalizedMonitoringError =
      monitoringError instanceof Error ? monitoringError : new Error(String(monitoringError));
    console.error('[monitoring] Sentry could not capture the i18n error', normalizedMonitoringError);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseLocaleResources(value: unknown, context: string): LocaleResources {
  if (!isRecord(value)) {
    throw new TypeError(`${context} must be an object`);
  }

  const resources: LocaleResources = {};
  for (const [namespace, translations] of Object.entries(value)) {
    if (!isRecord(translations)) {
      throw new TypeError(`${context}.${namespace} must be an object`);
    }
    resources[namespace] = translations;
  }
  return resources;
}

function parseBuildManifest(value: unknown): BuildManifest {
  if (!isRecord(value) || !Array.isArray(value.files)) {
    throw new TypeError('Prerender manifest must contain a files array');
  }

  const files = value.files.map((file, index) => {
    if (!isRecord(file) || typeof file.path !== 'string') {
      throw new TypeError(`Prerender manifest file ${index} must contain a path`);
    }
    return { path: file.path };
  });

  const manifest: BuildManifest = { files };
  if (value.localesHash !== undefined) {
    if (typeof value.localesHash !== 'string') {
      throw new TypeError('Prerender manifest localesHash must be a string');
    }
    manifest.localesHash = value.localesHash;
  }
  return manifest;
}

function addLocaleResources(language: AppLanguage, resources: LocaleResources): void {
  for (const [namespace, translations] of Object.entries(resources)) {
    i18n.addResourceBundle(language, namespace, translations, true, true);
  }
}

export function isAppLanguage(value: unknown): value is AppLanguage {
  return typeof value === 'string' && APP_LANGUAGES.some((language) => language === value);
}

export async function ensureLocale(language: AppLanguage): Promise<void> {
  if (loadedLocales.has(language)) {
    return;
  }

  const existingPromise = localeLoadPromises.get(language);
  if (existingPromise) {
    await existingPromise;
    return;
  }

  if (language === 'de') {
    loadedLocales.add(language);
    return;
  }

  const loadPromise = localeLoaders[language]()
    .then((module) => {
      addLocaleResources(language, parseLocaleResources(module.default, `Locale ${language}`));
      loadedLocales.add(language);
    })
    .catch((error: unknown) => {
      localeLoadPromises.delete(language);
      throw error;
    });

  localeLoadPromises.set(language, loadPromise);
  await loadPromise;
}

export async function changeAppLanguage(language: AppLanguage): Promise<void> {
  await ensureLocale(language);
  await i18n.changeLanguage(language);

  try {
    localStorage.setItem('i18nextLng', language);
  } catch (error) {
    reportI18nError(`Could not cache language ${language}`, error);
  }
}

export function applyLocaleOverrides(language: AppLanguage, resources: LocaleResources): void {
  addLocaleResources(language, resources);
}

export async function applyPrerenderedOverrides(language: AppLanguage): Promise<boolean> {
  if (import.meta.env.DEV) {
    return false;
  }

  const manifestResponse = await fetch('/generated/build-manifest.json');
  if (!manifestResponse.ok) {
    throw new Error(`Prerender manifest request failed with status ${manifestResponse.status}`);
  }

  const manifest = parseBuildManifest((await manifestResponse.json()) as unknown);
  const localesFile = manifest.files.find((file) => file.path.startsWith('locales-merged.'));
  if (!localesFile) {
    return false;
  }

  const localesResponse = await fetch(`/generated/${localesFile.path}`);
  if (!localesResponse.ok) {
    throw new Error(`Prerender locales request failed with status ${localesResponse.status}`);
  }

  const prerenderedLocales = (await localesResponse.json()) as unknown;
  if (!isRecord(prerenderedLocales)) {
    throw new TypeError('Prerender locales payload must be an object');
  }

  const localeResources = prerenderedLocales[language];
  if (localeResources === undefined) {
    throw new TypeError(`Prerender locales payload is missing locale ${language}`);
  }

  addLocaleResources(language, parseLocaleResources(localeResources, `Prerender locale ${language}`));
  console.info(`[i18n] Applied prerendered overrides for ${language} (hash: ${manifest.localesHash ?? 'unversioned'})`);
  return true;
}

const deResources = parseLocaleResources(deTranslations, 'Locale de');

/**
 * Resolve the locale for the very first render, synchronously and without a
 * network round-trip so a non-German instance never flashes German:
 * 1. the prerendered <html lang>, baked in per instance at build time,
 * 2. the language cached from a previous session,
 * 3. the always-bundled German base.
 */
function resolveBootstrapLanguage(): AppLanguage {
  if (typeof document !== 'undefined') {
    const htmlLang = document.documentElement.lang;
    if (isAppLanguage(htmlLang)) {
      return htmlLang;
    }
  }
  try {
    const cached = localStorage.getItem('i18nextLng');
    if (isAppLanguage(cached)) {
      return cached;
    }
  } catch {
    // localStorage can be unavailable (private mode); fall through to the base.
  }
  return 'de';
}

/** Platform language first, then English, then the always-bundled German base. */
function buildFallbackChain(primary: AppLanguage): AppLanguage[] {
  const chain: AppLanguage[] = [];
  for (const language of [primary, 'en', 'de'] as const) {
    if (!chain.includes(language)) {
      chain.push(language);
    }
  }
  return chain;
}

const bootstrapLanguage = resolveBootstrapLanguage();

const i18nInitialization = i18n
  .use(initReactI18next)
  .init({
    resources: { de: deResources },
    lng: bootstrapLanguage,
    fallbackLng: buildFallbackChain(bootstrapLanguage),
    defaultNS: 'common',
    debug: import.meta.env.VITE_I18N_DEBUG === 'true',
    showSupportNotice: false,
    initAsync: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  })
  .catch((error: unknown) => {
    reportI18nError('Initialization failed', error);
  });

/**
 * Resolves once the first-paint locale is loaded and active, so the initial
 * render is already in the platform language. German needs nothing extra (it is
 * statically bundled); any other language loads its chunk first. A load failure
 * degrades to the German fallback rather than blocking the app.
 */
export const i18nReady: Promise<void> = i18nInitialization
  .then(async () => {
    if (bootstrapLanguage === 'de') {
      return;
    }
    await changeAppLanguage(bootstrapLanguage);
  })
  .catch((error: unknown) => {
    reportI18nError(`Bootstrap to ${bootstrapLanguage} failed`, error);
  });

export default i18n;
